import { useRelayOwnership } from '@core/realtime/useRelayOwnership';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { getRithmicRelay, type RelayConnState } from '../lib/rithmicRelay';
import { deriveBadgeDisplay, normaliseRithmicState, type RithmicBeState } from '../lib/rithmicUiState';

/** GET /api/v1/rithmic/status response (also the `rithmic.state` push payload shape). Canonical copy —
 * import from here instead of re-declaring (the connect dialog used to carry a duplicate). */
export interface RithmicStatus {
  state: RithmicBeState;
  /** Account ids owned by the LIVE session (same keys as `AccountInfo.name`). Empty when no session. */
  accounts: string[];
  lastError: string | null;
  reason: string | null;
}

/**
 * The Rithmic connection state as the USER should perceive it — shared by the top-bar
 * `AccountConnectionControl` / Accounts panel and the connect dialog so they can never disagree.
 * One source of truth:
 * the `['rithmic','status']` query (60s poll), corrected in real time by `rithmic.state` pushes
 * (written into the SAME cache key), combined with THIS TAB's actual browser→Rithmic socket state —
 * "live" only when the BE session is live AND the socket is really open.
 */
export function useRithmicDisplayState(): {
  display: RithmicBeState;
  beState: RithmicBeState;
  relayConn: RelayConnState;
  /** Account ids owned by the live session — the per-account "is live" join key (Accounts panel). */
  accounts: string[];
  reason: string | null;
  lastError: string | null;
} {
  const api = useApiClient();
  const realtime = useRealtime();
  const queryClient = useQueryClient();
  const [relayConn, setRelayConn] = useState<RelayConnState>('closed');
  const ownership = useRelayOwnership('rithmic');

  const status = useQuery({
    queryKey: ['rithmic', 'status'],
    queryFn:  () => api.get<RithmicStatus>('/api/v1/rithmic/status'),
    refetchInterval: 60_000,
  });

  // Fold each rithmic.state push into the SHARED ['rithmic','status'] cache (same key the poll uses),
  // so every consumer reads one truth and the poll still corrects a stale push.
  useEffect(() => {
    return realtime.subscribe('rithmic.state', (payload) => {
      queryClient.setQueryData<RithmicStatus>(['rithmic', 'status'], (prev) => ({
        state: normaliseRithmicState(payload.state),
        accounts: payload.accounts ?? prev?.accounts ?? [],
        lastError: payload.lastError ?? null,
        reason: payload.reason ?? null,
      }));
    });
  }, [realtime, queryClient]);

  // Track the ACTUAL browser→Rithmic socket for this tab.
  useEffect(() => {
    const relay = getRithmicRelay();
    setRelayConn(relay.getConnectionState());
    return relay.onConnectionChange(() => setRelayConn(relay.getConnectionState()));
  }, []);

  const beState = normaliseRithmicState(status.data?.state);
  return {
    display: deriveBadgeDisplay(beState, relayConn, ownership.mode === 'passive'),
    beState,
    relayConn,
    accounts: status.data?.accounts ?? [],
    reason: status.data?.reason ?? null,
    lastError: status.data?.lastError ?? null,
  };
}
